import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CatalogueApiService, DiscussionApiService } from '@angular-workspace/services';
import { DiscussionFormData } from '../create-discussion-offcanvas/create-discussion-offcanvas.component';
import { FormatService } from '../../services/format.service';

export interface AssetSearchResult {
  id: string;
  name: string;
  label?: string;
  type: string;
  organization?: string;
}

export interface UploadedAttachment {
  file?: File; // optional for existing attachments from API
  key: string;
  name?: string; // display name - from file.name or API response
  uploading?: boolean;
  error?: string;
}

@Component({
  selector: 'app-create-discussion-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './create-discussion-form.component.html',
  styleUrl: './create-discussion-form.component.scss',
})
export class CreateDiscussionFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() initialValues?: Partial<DiscussionFormData>;
  /** When set, used as discussion id for attachment presigned URL (e.g. existing discussion). When creating new, a session id is used. */
  @Input() discussionId?: string;
  /** When true, show "Update" instead of "Publish" on the submit button */
  @Input() isEditMode: boolean = false;
  @Output() publish = new EventEmitter<DiscussionFormData>();
  @Output() cancel = new EventEmitter<void>();

  private catalogueApiService = inject(CatalogueApiService);
  private discussionApiService = inject(DiscussionApiService);
  private fb = inject(FormBuilder);
  private formatService = inject(FormatService);
  private searchSubject = new Subject<string>();

  discussionForm!: FormGroup;
  tagInput: string = '';
  assetSearchResults: AssetSearchResult[] = [];
  showAssetSearchResults: boolean = false;
  selectedAsset: AssetSearchResult | null = null;
  attachmentFiles: UploadedAttachment[] = [];
  assetSearchQuery: string = '';
  isLoadingAssets: boolean = false;
  assetCategoryDropdownOpen: boolean = false;
  /** Session id for attachment uploads when creating a new discussion (no discussionId yet). */
  attachmentSessionId: string = '';

  categoryOptions = [
    { value: 'GETTING_STARTED', label: 'Getting Started' },
    { value: 'PUBLIC', label: 'Public' },
    { value: 'GENERAL', label: 'General' },
    { value: 'PRODUCT_FEEDBACK', label: 'Feedback' },
    { value: 'PRODUCT_ANNOUNCEMENTS', label: 'Announcement' }
  ];

  // All available asset category options (Usecases disabled for new discussions)
  allAssetCategoryOptions = [
    { value: 'Dataset', label: 'Datasets' },
    { value: 'Model', label: 'Models' }
  ];

  // Filtered asset category options based on selected discussion category
  get assetCategoryOptions(): { value: string; label: string }[] {
    const selectedCategory = this.discussionForm.get('category')?.value;
    
    // If no category is selected, show all options
    if (!selectedCategory) {
      return this.allAssetCategoryOptions;
    }
    
    // Return all options for now (can be customized based on category if needed)
    // For example, certain categories might only show specific asset types
    return this.allAssetCategoryOptions;
  }

  readonly CONTENT_MAX_LENGTH = 5000;
  readonly CONTENT_MIN_LENGTH = 100;
  readonly TITLE_MIN_LENGTH = 30;
  readonly TITLE_MAX_LENGTH = 200;

  /** Allowed attachment types: PDF, JPG, PNG. Max 5MB per file. */
  readonly ALLOWED_ATTACHMENT_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
  readonly MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

  getAllowedFileTypesLabel(): string {
    return 'PDF, JPG, PNG';
  }

  ngOnInit(): void {
    this.initDiscussionForm();
    this.setupAssetSearch();
    // Session id for attachment presigned URL when creating new discussion (no discussionId yet)
    this.attachmentSessionId = this.discussionId || this.generateSessionId();
    // Apply initial values after form exists (e.g. category from URL when opening from a category)
    if (this.initialValues && Object.keys(this.initialValues).length > 0) {
      this.applyInitialValues();
    }
    this.applyCategoryEditLock();
  }

  private generateSessionId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['discussionId'] && this.discussionId) {
      this.attachmentSessionId = this.discussionId;
    }
    if (changes['initialValues'] && this.initialValues && this.discussionForm) {
      this.applyInitialValues();
    }
    if (
      (changes['isEditMode'] || changes['initialValues']) &&
      this.discussionForm
    ) {
      this.applyCategoryEditLock();
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  initDiscussionForm(): void {
    this.discussionForm = this.fb.group({
      category: ['', [Validators.required]],
      title: ['', [Validators.required, Validators.minLength(30), Validators.maxLength(200)]],
      assetCategory: [''],
      assetId: [''],
      assetName: [''],
      content: ['', [Validators.required, Validators.minLength(100)]],
      tags: [[], [Validators.required]],
      attachments: [[]]
    });
  }

  applyInitialValues(): void {
    if (this.initialValues && this.discussionForm) {
      this.discussionForm.patchValue({
        category: this.initialValues.category || '',
        assetCategory: this.initialValues.assetCategory || '',
        assetId: this.initialValues.assetId || '',
        assetName: this.initialValues.assetName || '',
        title: this.initialValues.title || '',
        content: this.initialValues.content || '',
        tags: this.initialValues.tags || []
      });
      
      if (this.initialValues.assetName) {
        this.assetSearchQuery = this.initialValues.assetName;
        this.selectedAsset = this.initialValues.assetId ? {
          id: this.initialValues.assetId,
          name: this.initialValues.assetName,
          type: this.initialValues.assetCategory || ''
        } : null;
      }
      
      // Load existing attachments from API (edit mode)
      const existing = this.initialValues.existingAttachments;
      if (existing && Array.isArray(existing) && existing.length > 0) {
        this.attachmentFiles = existing.map((a: { key: string; name: string }) => ({
          key: a.key,
          name: a.name,
          uploading: false
        }));
      } else if (this.initialValues.attachments && Array.isArray(this.initialValues.attachments)) {
        this.attachmentFiles = [];
      }
    }
  }

  /** Get display name for attachment (from file or API) */
  getAttachmentDisplayName(attachment: UploadedAttachment): string {
    return attachment.file?.name ?? attachment.name ?? attachment.key ?? 'Attachment';
  }

  setupAssetSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: string) => {
        if (!query || query.trim().length < 2) {
          this.assetSearchResults = [];
          this.showAssetSearchResults = false;
          this.isLoadingAssets = false;
          return Promise.resolve([]);
        }
        return this.searchAssets(query);
      })
    ).subscribe();
  }

  async searchAssets(query: string): Promise<AssetSearchResult[]> {
    const assetCategory = this.discussionForm.get('assetCategory')?.value;
    if (!assetCategory || assetCategory === 'Others') {
      this.assetSearchResults = [];
      this.showAssetSearchResults = false;
      this.isLoadingAssets = false;
      return Promise.resolve([]);
    }

    if (!query || !query.trim()) {
      this.showAssetSearchResults = false;
      this.assetSearchResults = [];
      this.isLoadingAssets = false;
      return Promise.resolve([]);
    }

    try {
      this.isLoadingAssets = true;
      
      const assetTypeMap: { [key: string]: string[] } = {
        'Dataset': ['Dataset'],
        'Model': ['Model'],
        'Usecase': ['Usecase']
      };

      const assetTypes = assetTypeMap[assetCategory] || [];
      
      const response = await this.catalogueApiService.searchAllAssets(
        query.trim(),
        assetTypes,
        [], // customFilter
        1, // page
        10, // size - limit to 10 suggestions
        'name:asc;itemCreatedAt:asc;',
        [] // additional searchCriteria
      );

      // Extract items from response - handle multiple possible response structures
      let items: any[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response?.result && Array.isArray(response.result)) {
        items = response.result;
      } else if (response?.data && Array.isArray(response.data)) {
        items = response.data;
      } else if (response?.items && Array.isArray(response.items)) {
        items = response.items;
      }

      // Map to AssetSearchResult format (include title for APIs that use it)
      const results = items
        .filter(item => item.id || item['@id'])
        .map((item: any) => {
          const displayName = item.label || item.name || item.title || 'Untitled';
          return {
            id: item.id || item['@id'] || '',
            name: displayName,
            label: displayName,
            type: assetCategory,
            organization: item.provider || item.organisation || item.organization || item.provider_name || ''
          };
        });

      this.assetSearchResults = results;
      this.showAssetSearchResults = true;
      return results;
    } catch (error) {
      console.error('Error searching assets:', error);
      this.assetSearchResults = [];
      this.showAssetSearchResults = true;
      return [];
    } finally {
      this.isLoadingAssets = false;
    }
  }

  onAssetSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const query = input.value.trim();
    this.assetSearchQuery = input.value; // Keep the full value for display
    if (query.length >= 2) {
      this.searchSubject.next(query);
    } else {
      this.assetSearchResults = [];
      this.showAssetSearchResults = false;
      this.isLoadingAssets = false;
    }
  }

  onAssetSearchBlur(): void {
    setTimeout(() => {
      this.showAssetSearchResults = false;
    }, 200);
  }

  onAssetSearchFocus(): void {
    const query = this.assetSearchQuery.trim();
    if (query.length < 2) return;
    if (this.assetSearchResults.length > 0 || this.showAssetSearchResults) {
      this.showAssetSearchResults = true;
    } else if (!this.isLoadingAssets) {
      this.searchSubject.next(query);
    }
  }

  selectAsset(asset: AssetSearchResult): void {
    this.selectedAsset = asset;
    this.discussionForm.patchValue({
      assetId: asset.id,
      assetName: asset.name || asset.label || ''
    });
    this.showAssetSearchResults = false;
    this.assetSearchQuery = asset.name || asset.label || '';
  }

  clearAsset(): void {
    this.selectedAsset = null;
    this.assetSearchQuery = '';
    this.discussionForm.patchValue({
      assetId: '',
      assetName: ''
    });
  }

  onAssetCategoryChange(): void {
    this.clearAsset();
    this.assetSearchResults = [];
    this.showAssetSearchResults = false;
  }

  toggleAssetCategoryDropdown(): void {
    this.assetCategoryDropdownOpen = !this.assetCategoryDropdownOpen;
  }

  selectAssetCategory(value: string): void {
    this.discussionForm.patchValue({ assetCategory: value });
    this.clearAsset();
    this.assetSearchResults = [];
    this.showAssetSearchResults = false;
    this.assetCategoryDropdownOpen = false;
  }

  getSelectedAssetCategoryLabel(): string {
    const value = this.discussionForm.get('assetCategory')?.value;
    if (!value) return 'Select asset type';
    const option = this.assetCategoryOptions.find(opt => opt.value === value);
    return option ? option.label : 'Select asset type';
  }

  private applyCategoryEditLock(): void {
    const categoryControl = this.discussionForm?.get('category');
    if (!categoryControl) return;
    if (this.isEditMode) {
      categoryControl.disable({ emitEvent: false });
    } else {
      categoryControl.enable({ emitEvent: false });
    }
  }

  onCategoryChange(value: string): void {
    if (this.isEditMode) return;
    const currentValue = this.discussionForm.get('category')?.value;
    // Single select: if clicking the same checkbox, uncheck it; otherwise select the new one
    if (currentValue === value) {
      this.discussionForm.patchValue({ category: '' });
    } else {
      this.discussionForm.patchValue({ category: value });
    }
    this.discussionForm.get('category')?.markAsTouched();
  }

  getAssetCategoryLabel(value: string): string {
    const option = this.assetCategoryOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  getAssetSearchEmptyMessage(): string {
    const category = this.discussionForm.get('assetCategory')?.value;
    const label = category
      ? this.getAssetCategoryLabel(category)
      : 'assets';
    return `No ${label} found.`;
  }

  getTags(): string[] {
    return this.discussionForm.get('tags')?.value || [];
  }

  /** Highlight tag chip when input text matches it (same as dataset creation). */
  isTagDuplicate(tag: string): boolean {
    const input = (this.tagInput || '').trim().toLowerCase();
    return input.length > 0 && tag.toLowerCase() === input;
  }

  addTag(): void {
    const tag = this.sanitizeTagInput(this.tagInput.trim());
    this.tagInput = tag;
    if (!tag || !this.isValidTagValue(tag)) {
      return;
    }
    const currentTags = this.getTags();
    if (!currentTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
      currentTags.push(tag);
      this.discussionForm.patchValue({ tags: currentTags });
      this.discussionForm.get('tags')?.markAsTouched();
      this.tagInput = '';
    }
  }

  removeTag(tagToRemove: string): void {
    const currentTags = this.getTags();
    const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
    this.discussionForm.patchValue({ tags: updatedTags });
    this.discussionForm.get('tags')?.markAsTouched();
  }

  onTagInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTagInputChange(): void {
    const sanitized = this.sanitizeTagInput(this.tagInput);
    if (sanitized !== this.tagInput) {
      this.tagInput = sanitized;
    }
  }

  onTagInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.tagInput = this.sanitizeTagInput(this.tagInput + pasted);
  }

  canAddTag(): boolean {
    const tag = this.tagInput.trim();
    return tag.length > 0 && this.isValidTagValue(tag);
  }

  private sanitizeTagInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  private isValidTagValue(tag: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(tag);
  }

  private getAttachmentValidationError(file: File): string | null {
    const ext = file.name.includes('.') ? '.' + file.name.split('.').pop()!.toLowerCase() : '';
    if (!this.ALLOWED_ATTACHMENT_EXTENSIONS.some(e => ext === e)) {
      return `Allowed formats: ${this.getAllowedFileTypesLabel()}`;
    }
    if (file.size > this.MAX_ATTACHMENT_SIZE_BYTES) {
      return `Max file size 5MB (${(file.size / (1024 * 1024)).toFixed(2)}MB selected)`;
    }
    return null;
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const files = Array.from(input.files);
    input.value = '';

    const newAttachments: UploadedAttachment[] = files.map(file => {
      const validationError = this.getAttachmentValidationError(file);
      return {
        file,
        key: '',
        name: file.name,
        uploading: !validationError,
        error: validationError ?? undefined
      };
    });

    this.attachmentFiles = [...this.attachmentFiles, ...newAttachments];
    this.discussionForm.patchValue({ attachments: this.attachmentFiles });

    const discussionIdForUpload = this.discussionId || this.attachmentSessionId;
    files.forEach((file, index) => {
      const validationError = this.getAttachmentValidationError(file);
      if (validationError) return;

      const attachmentIndex = this.attachmentFiles.length - files.length + index;
      (async () => {
        try {
          const presignedResponse = await this.discussionApiService.discussionAttachment(
            discussionIdForUpload,
            { type_of_upload: 'attachment', file_name: file.name },
            file
          );
          const objectKey = presignedResponse?.meta?.object_key ?? presignedResponse?.data?.object_key ?? (presignedResponse as any)?.object_key;
          if (!objectKey) {
            throw new Error(presignedResponse?.message ?? (presignedResponse as any)?.detail ?? 'No object key in response');
          }
          if (this.attachmentFiles[attachmentIndex]) {
            const att = this.attachmentFiles[attachmentIndex];
            this.attachmentFiles = this.attachmentFiles.slice();
            this.attachmentFiles[attachmentIndex] = {
              ...att,
              key: objectKey,
              name: att.file?.name ?? att.name,
              uploading: false
            };
            this.discussionForm.patchValue({ attachments: this.attachmentFiles });
          }
        } catch (error: any) {
          console.error('Error uploading file:', file.name, error);
          const message =
            error?.error?.detail ??
            error?.error?.message ??
            (typeof error?.error === 'string' ? error.error : null) ??
            error?.message ??
            'Failed to upload file';
          if (this.attachmentFiles[attachmentIndex]) {
            this.attachmentFiles = this.attachmentFiles.slice();
            this.attachmentFiles[attachmentIndex] = {
              ...this.attachmentFiles[attachmentIndex],
              uploading: false,
              error: message
            };
            this.discussionForm.patchValue({ attachments: this.attachmentFiles });
          }
        }
      })();
    });
  }

  removeAttachment(index: number): void {
    this.attachmentFiles.splice(index, 1);
    this.discussionForm.patchValue({ attachments: this.attachmentFiles });
  }
  
  getAttachmentStatus(attachment: UploadedAttachment): string {
    if (attachment.uploading) {
      return 'Uploading...';
    }
    if (attachment.error) {
      return 'Error';
    }
    if (attachment.key) {
      return 'Uploaded';
    }
    return 'Pending';
  }

  /**
   * Check if there are any successfully uploaded files (with 201 status - have keys)
   * Hide upload option if any files are successfully uploaded
   */
  hasUploadedFiles(): boolean {
    return this.attachmentFiles.some(att => att.key && !att.error);
  }

  get contentLength(): number {
    const content = this.discussionForm?.get('content')?.value || '';
    return content.length || 0;
  }

  get titleLength(): number {
    const title = this.discussionForm?.get('title')?.value || '';
    return title.length || 0;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  cancelForm(): void {
    this.cancel.emit();
    this.resetForm();
  }

  resetForm(): void {
    this.discussionForm.reset();
    this.tagInput = '';
    this.selectedAsset = null;
    this.assetSearchQuery = '';
    this.assetSearchResults = [];
    this.showAssetSearchResults = false;
    this.attachmentFiles = [];
    
    if (this.initialValues) {
      setTimeout(() => this.applyInitialValues(), 0);
    }
  }

  publishDiscussion(): void {
    if (this.discussionForm.valid) {
      // Check if any files are still uploading
      const uploadingFiles = this.attachmentFiles.filter(att => att.uploading);
      if (uploadingFiles.length > 0) {
        console.warn('Some files are still uploading. Please wait...');
        return;
      }
      
      // Check if any files failed to upload
      const failedFiles = this.attachmentFiles.filter(att => att.error);
      if (failedFiles.length > 0) {
        console.error('Some files failed to upload:', failedFiles.map(f => this.getAttachmentDisplayName(f)));
        // You might want to show an error message to the user here
      }
      
      // Extract only successfully uploaded file keys
      const uploadedKeys = this.attachmentFiles
        .filter(att => att.key && !att.error)
        .map(att => att.key);
      
      // Get assetId and assetName from form, with fallback to selectedAsset
      let assetId = this.discussionForm.get('assetId')?.value || '';
      let assetName = this.discussionForm.get('assetName')?.value || '';
      
      // Fallback: if form values are empty but selectedAsset exists, use it
      if ((!assetId || !assetName) && this.selectedAsset) {
        assetId = this.selectedAsset.id || assetId;
        assetName = this.selectedAsset.name || this.selectedAsset.label || assetName;
      }
      
      console.log('Form values before emitting:', {
        assetCategory: this.discussionForm.get('assetCategory')?.value,
        assetId: assetId,
        assetName: assetName,
        selectedAsset: this.selectedAsset,
        formAssetId: this.discussionForm.get('assetId')?.value,
        formAssetName: this.discussionForm.get('assetName')?.value
      });
      
      const formData: DiscussionFormData = {
        category: this.discussionForm.getRawValue().category || '',
        title: this.formatService.stripAllHtml(this.discussionForm.get('title')?.value || '').trim(),
        assetCategory: this.discussionForm.get('assetCategory')?.value || '',
        assetId: assetId,
        assetName: assetName,
        content: this.discussionForm.get('content')?.value || '',
        tags: this.discussionForm.get('tags')?.value || [],
        attachments: uploadedKeys as any // Pass the keys instead of File objects
      };
      
      console.log('Emitting formData:', formData);
      this.publish.emit(formData);
    } else {
      Object.keys(this.discussionForm.controls).forEach(key => {
        this.discussionForm.get(key)?.markAsTouched();
      });
    }
  }
}
